import { setInbox } from '../../state';
import API_CALL from '../API';
import { Inbox } from '../share/ShareService';

// Definizione delle interfacce
export interface GrantRequest {
  name?: string;
  expires_on?: number;
  recipient?: string;
  message?: string;
}

export interface GrantItem {
  id: string;
  user_id: number;
  /** Oggetto della richiesta */
  name: string;
  /** Destinatario della richiesta */
  recipient: string;
  kind: number;
  created_on: number;
  expires_on: number;
  sent_on: null;
  /** Messaggio della richiesta */
  message: string;
  usage_count: number;
  state: 'pending' | 'used' | 'expired';
  type: 'inbox';
  status?: 'sharing' | 'deleting' | 'idle';
}

// Nuove interfacce per gli inbox
export interface InboxResponse {
  used_space: number;
  grant_count: number;
  content_count: number;
  new_content_count: number;
}

export interface InboxContent {
  content_id: string;
  filename: string;
  content_md5: string;
  content_type: string;
  content_length: number;
  uploaded_on: number;
  object_name: string;
  grant_id: string;
  grant_name: string;
  grant_recipient: string;
  download_count: number;
  auto_delete_on: null;
  status?: 'sharing' | 'deleting' | 'idle';
  downloading?: boolean;
}

interface InboxDownloadResponse {
  user_id: number;
  content_id: string;
  filename: string;
  content_md5: string;
  content_type: string;
  content_length: number;
  uploaded_on: number;
  object_name: string;
  grant_id: string;
  grant_name: string;
  grant_recipient: string;
  download_count: number;
  auto_delete_on: null;
  download_url: string;
  download_url_expires: number;
}

// Funzioni del servizio
/**
 * Ottiene la lista dei grant attivi
 */
const getGrantList = async (): Promise<GrantItem[]> => {
  return await API_CALL({
    url: '/inbox/grant',
    method: 'GET',
  }).then((res) => {
    return res.map(parseInboxGrant);
  });
};

/**
 * Crea una nuova richiesta di grant
 */
const createGrant = async (grantData: GrantRequest): Promise<GrantItem> => {
  return await API_CALL({
    url: '/inbox/grant',
    method: 'POST',
    data: grantData,
  });
};

/**
 * Invia una richiesta di grant esistente
 */
const sendGrant = async (grantId: string): Promise<void> => {
  return await API_CALL({
    url: `/inbox/grant/${grantId}/send`,
    method: 'POST',
  });
};

/**
 * Ottiene la lista dei messaggi in inbox
 */
const getInboxList = async (): Promise<InboxResponse> => {
  return await API_CALL({
    url: '/inbox',
    method: 'GET',
  });
};

/**
 * Ottiene i contenuti disponibili nell'inbox
 */
const getInboxContent = async (): Promise<InboxContent[]> => {
  return await API_CALL({
    url: '/inbox/content',
    method: 'GET',
  }).then((res) => {
    return res.map(parseInboxGrant);
  });
};

const parseInboxGrant = (grant: GrantItem) => {
  // Imposta il campo 'state' sui grant nella lista,
  // confrontando il il ts di scadenza con quello attuale
  var currentTs = Math.floor(Date.now() / 1000);
  if (grant.usage_count > 0) {
    grant.state = 'used';
  } else {
    grant.state = grant.expires_on > currentTs ? 'pending' : 'expired';
  }
  grant.type = 'inbox';
  return grant;
};

/**
 * Cancella un contenuto dall'inbox
 */
const deleteInboxContent = async (contentId: string): Promise<void> => {
  return await API_CALL({
    url: `/inbox/grant/${contentId}`,
    method: 'DELETE',
  });
};

/**
 * Aggiorna il contenuto dell'inbox
 * @param grantId - ID del grant da aggiornare
 * @param content - Contenuto da aggiornare
 * @param autosend - Se true, invia automaticamente il grant dopo l'aggiornamento
 */
const updateInboxContent = async (
  grantId: string,
  content: Partial<GrantRequest>,
  autosend?: boolean
): Promise<void> => {
  return await API_CALL({
    url: `/inbox/grant/${grantId}${autosend ? '?autosend=true' : ''}`,
    method: 'PUT',
    data: content,
  });
};

const downloadFile = async (
  file: InboxContent
): Promise<InboxDownloadResponse> => {
  return await API_CALL({
    url: `/inbox/content/${file.content_id}`,
    method: 'GET',
  });
};
const getInboxes = async (): Promise<Inbox> => {
  const grants = await getGrantList();
  const contents = await getInboxContent();
  console.log('🚀 ~ getInboxes ~ contents:', contents);
  const inbox = {
    grants,
    contents,
  };
  setInbox(inbox);
  return inbox;
};
export const InboxService = {
  getGrantList,
  createGrant,
  sendGrant,
  getInboxList,
  getInboxContent,
  updateInboxContent,
  downloadFile,
  deleteInboxContent,
  getInboxes,
};

export default InboxService;
